import React, { useEffect } from "react"
import { CalculationSchema } from "src/features/calculator/schema"
import { UseFormSetValue, UseFormWatch, FieldErrors } from "react-hook-form"
import styled from "styled-components"
import {
  calcTotalReimbursement,
  calcSelfPayment,
  calcCustomerTransferFee,
  calcRemittance,
} from "src/features/calculator/utils/formulas/basicFormula"
import {
  calcCustomerTransferFeeRateWithAD,
  calcCustomerTransferFeeRateWithoutAD,
} from "src/features/calculator/utils/formulas/riahouseFormula"
import { TotalReimbursementRow } from "src/features/calculator/components/formula/row/TotalReimbursementRow"
import { SelfPaymentRow } from "src/features/calculator/components/formula/row/SelfPaymentRow"
import { CustomerTransferFeeRow } from "src/features/calculator/components/formula/row/CustomerTransferFeeRow"
import { RemittanceRow } from "src/features/calculator/components/formula/row/RemittanceRow"
import { useCalculatedResult } from "src/features/calculator/hooks/useCalculatedResult"
import { FormField } from "src/features/calculator/components/field/FormField"
import { useFormValueUpdate } from "src/features/calculator/hooks/useFormValueUpdate"

type Props = {
  setValue: UseFormSetValue<CalculationSchema>
  watch: UseFormWatch<CalculationSchema>
  errors: FieldErrors<CalculationSchema>
  withAd?: boolean
}

export const RiahouseFormula = ({ setValue, watch, errors, withAd = false }: Props) => {
  const watchedValues = watch()
  const {
    customerTransferFeeRate,
    totalReimbursement,
    customerTransferFee,
    maxAdvancePayment,
    initialCost,
    downPayment,
    rent,
  } = watch()

  function calcCustomerTransferFeeRate() {
    return withAd
      ? calcCustomerTransferFeeRateWithAD({ rent })
      : calcCustomerTransferFeeRateWithoutAD({ rent })
  }

  const calculatedResult = useCalculatedResult({
    values: watchedValues,
    setValue,
    calculation: {
      customerTransferFeeRate: calcCustomerTransferFeeRate(),
      totalReimbursement: calcTotalReimbursement({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      selfPayment: calcSelfPayment({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      customerTransferFee: calcCustomerTransferFee({
        rent,
        customerTransferFeeRate,
      }),
      remittance: calcRemittance({
        totalReimbursement,
        customerTransferFee,
      }),
    },
  })

  useEffect(() => {
    const newCustomerTransferFeeRate = calcCustomerTransferFeeRate()
    if (customerTransferFeeRate !== newCustomerTransferFeeRate) {
      setValue("customerTransferFeeRate", newCustomerTransferFeeRate)
    }
  }, [withAd, setValue, calculatedResult.customerTransferFeeRate])

  useFormValueUpdate({
    calculatedValue: calculatedResult.customerTransferFee,
    currentValue: customerTransferFee,
    fieldName: "customerTransferFee",
    setValue,
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.totalReimbursement,
    currentValue: totalReimbursement,
    fieldName: "totalReimbursement",
    setValue,
  })

  return (
    <StyledCalculatorDiv>
      <StyledCalculatorRowDiv>
        <FormField
          label="送客手数料率(税込)"
          type="number"
          placeholder="Placeholder"
          hint={`${calculatedResult.customerTransferFeeRate}%`}
          disabled
          value={calculatedResult.customerTransferFeeRate}
        />
        <span>= もし</span>
        <FormField
          label="家賃"
          type="number"
          placeholder="Placeholder"
          hint={`${rent.toLocaleString()}円`}
          onChange={(e) => {
            setValue("rent", Number(e.target.value))
          }}
          errorText={errors?.rent?.message}
          value={rent}
        />
        <span>
          {withAd ? `> 60000 ならば 0.77 それ以外は 0.7` : `> 60000 ならば 0.66 それ以外は 0.6`}
        </span>
      </StyledCalculatorRowDiv>

      <TotalReimbursementRow
        totalReimbursement={calculatedResult.totalReimbursement}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <SelfPaymentRow
        selfPayment={calculatedResult.selfPayment}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <CustomerTransferFeeRow
        customerTransferFee={calculatedResult.customerTransferFee}
        rent={rent}
        customerTransferFeeRate={customerTransferFeeRate}
        setValue={setValue}
        errors={errors}
      />
      <RemittanceRow
        remittance={calculatedResult.remittance}
        totalReimbursement={totalReimbursement}
        customerTransferFee={customerTransferFee}
        setValue={setValue}
        errors={errors}
      />
    </StyledCalculatorDiv>
  )
}

const StyledCalculatorDiv = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StyledCalculatorRowDiv = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`
